import { useCallback, useRef } from 'react';

const MIN_CONFIDENCE = 0.6;

// eslint-disable-next-line import/prefer-default-export
export function useTranscricaoService({ onTranscript, onDebug }) {
  const socketRef = useRef(null);

  const emitDebug = useCallback((action) => {
    if (!onDebug || !action) return;
    onDebug(action);
  }, [onDebug]);

  const connect = useCallback(() => {
    if (socketRef.current) return socketRef.current;

    emitDebug({ type: 'STATUS', status: 'connecting', message: 'Conectando ao Deepgram...' });

    const socket = new WebSocket(
      'wss://api.deepgram.com/v1/listen' +
        '?model=nova-2&language=en-US&encoding=linear16&sample_rate=16000' +
        '&punctuate=false&smart_format=false&interim_results=true',
      ['token', 'f2e45d1e7a9f9a0d36b560e2d50aad8fc83a7dd5']
    );
    socketRef.current = socket;

    socket.addEventListener('open', () => {
      emitDebug({ type: 'STATUS', status: 'open', message: 'Conexão com Deepgram aberta' });
    });

    socket.addEventListener('message', (event) => {
      try {
        const msg = JSON.parse(event.data);
        const isFinal = msg.is_final;
        const alternative = msg.channel?.alternatives?.[0];
        const transcript = alternative?.transcript;
        const confidence = alternative?.confidence || 0;

        if (transcript) {
          emitDebug({
            type: 'TRANSCRIPT',
            transcript,
            timestamp: Date.now(),
            message: `Transcrição${isFinal ? ' final' : ''}: "${transcript}"`,
          });
        }

        if (transcript && isFinal && confidence >= MIN_CONFIDENCE) {
          onTranscript({
            text: transcript,
            isFinal,
            confidence,
            timestamp: Date.now(),
          });
        }
      } catch (err) {
        emitDebug({ type: 'ERROR', error: err?.message || 'Erro parseando payload da Deepgram' });
      }
    });

    socket.addEventListener('error', (err) => {
      emitDebug({ type: 'ERROR', error: err?.message || 'Erro na conexão com Deepgram' });
    });

    socket.addEventListener('close', (event) => {
      emitDebug({
        type: 'STATUS',
        status: 'closed',
        message: `Conexão fechada (code=${event?.code || 'n/d'})`,
      });
      socketRef.current = null;

      if (!event?.wasClean) {
        setTimeout(() => {
          emitDebug({
            type: 'STATUS',
            status: 'reconnecting',
            message: 'Tentando reconectar...',
          });
          connect();
        }, 1200);
      }
    });

    return socket;
  }, [emitDebug, onTranscript]);

  const send = useCallback((data) => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(data);
    }
  }, []);

  const disconnect = useCallback(() => {
    const socket = socketRef.current;
    if (socket) {
      emitDebug({ type: 'STATUS', status: 'closing', message: 'Encerrando conexão com Deepgram' });
      socket.close();
      socketRef.current = null;
    }
  }, [emitDebug]);

  return { connect, send, disconnect };
}
